import PropTypes from 'prop-types';
import React, { useRef, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button } from '@ipc/ipc-components';
import {
  getMrdContactsData,
  getMrdContactsIsLoading,
  getMrdContactsImpcData,
  getMrdContactsImpcColumns,
  getMrdContactsImpcIsLoading,
  getDeleteContactRequestLoading,
  getContactIMPC,
} from '../redux/selectors/MrdContactsSelectors';
import {
  postCopyContactRequestActionCreator,
  deleteContactRequestActionCreator,
  addNewContactRequestActionCreator,
  editContactRequestActionCreator,
} from '../redux/actions/ContactsActions';
import {
  associateMrdContactsRequestActionCreator,
} from '../../device-information/contact-management/redux/actions/ContactManagementActions';
import TableDefinitions from '../../../../common/TableDefinitions';
import ListTable from '../../../../common/list-table/components/ListTable';
import { MrdContactsStyles } from '../component/MrdContacts.styles';
import { tableLoadData } from '../../../../common/list-table/actions/ListTableActions';
import ContactCopyDialog from '../copy-contacts/component/ContactCopyDialog';
import { getUserRole } from '../../../../common/user/redux/selectors/UserSelectors';
import MrdContactsColumns from './MrdContactsColumns';
import ConfirmationDialog from '../../../../common/list-table/helpers/confirmationDialog';
import { formatMessage } from '../../../../../locales/locales';
import MrdContactsCreateDialog from '../create/component/MrdContactsCreateDialog';
import EditContactDialog from '../edit-contact/component/EditContactDialog';

function MrdContactsContainer(props) {
  const {
    isLoading,
    data,
    mrdImpcColumns,
    mrdImpcIsLoading,
    mrdImpcData,
    tableLoad,
    userRole,
    deleteContact,
    copyContactsRequest,
    addContact,
    editContact,
    preservedImpc,
    associateMrdRequest,
  } = props;

  const [selectedMrdImpc, updateSelectedMrdImpc] = useState('');
  const [showCopyContactsDialog, updateCopyContactsStatus] = useState(false);
  const [deleteVisible, updateDeleteVisibility] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [rowToEdit, setRowToEdit] = useState(null);
  const [editVisible, updateEditVisibility] = useState(false);
  const lastLoadedImpc = useRef(selectedMrdImpc);
  const [showAddNewContactDialog, updateShowAddNewContactDialog] = useState(false);
  const [editDuplicateVisible, updateEditDuplicateVisibility] = useState(false);
  useEffect(() => {
    tableLoad(TableDefinitions.MRD_IMPC_LIST_BY_COUNTRY.id);
  }, []);

  useEffect(() => {
    if (mrdImpcData.length > 0 && selectedMrdImpc === '') {
      updateSelectedMrdImpc(mrdImpcData[0].impc);
    } else if (selectedMrdImpc !== '' && lastLoadedImpc.current !== selectedMrdImpc) {
      tableLoad(TableDefinitions.MRD_CONTACTS.id, { impcCode: selectedMrdImpc });
      lastLoadedImpc.current = selectedMrdImpc;
    }
  }, [mrdImpcData, selectedMrdImpc]);

  useEffect(() => {
    if (preservedImpc && preservedImpc !== selectedMrdImpc) {
      updateSelectedMrdImpc(preservedImpc);
    }
  }, [preservedImpc]);

  const getOnClickOfMrdImpcTable = (state, rowInfo) => {
    if (rowInfo) {
      return {
        style: {
          background: rowInfo.original.impc === selectedMrdImpc ? '#dbffdb' : null,
        },
        onClick: () => {
          updateSelectedMrdImpc(rowInfo.original.impc);
        },
      };
    }
    return {};
  };
  const copyContactConfirm = (sourceImpc) => {
    const requestData = {
      sourceImpcCode: sourceImpc,
      targetImpcCode: selectedMrdImpc,
    };
    copyContactsRequest(requestData);
  };

  const handleDeleteDialogCancel = () => {
    updateDeleteVisibility(false);
  };
  const handleConfirmDelete = async () => {
    await deleteContact({
      contactGroup: 'MRD',
      contactId: rowToDelete,
      impcCode: selectedMrdImpc,
    });
    updateDeleteVisibility(false);
  };

  const onRowDelete = (row) => {
    setRowToDelete(row.id);
    updateDeleteVisibility(true);
  };

  const onRowEdit = (row) => {
    setRowToEdit(row);
    updateEditVisibility(true);
  };

  const openNewContactDialog = () => {
    updateShowAddNewContactDialog(true);
  };

  const closeNewContactDialog = () => {
    updateShowAddNewContactDialog(false);
  };

  const confirmAddNewContact = async (requestData) => {
    if (requestData.isEmailDuplicate) {
      setRowToEdit({
        id: requestData.isEmailDuplicate,
        emailAddress: requestData.emailAddress,
      });
      updateEditDuplicateVisibility(true);
    } else {
      await addContact({
        ...requestData,
        contactGroup: 'MRD',
        impcCode: selectedMrdImpc,
      });
    }
  };
  const confirmEditContact = async (requestData) => {
    await editContact({
      ...requestData,
      contactGroup: 'MRD',
      impcCode: selectedMrdImpc,
    });
  };
  const handleEditDuplicateDialogCancel = () => {
    updateEditDuplicateVisibility(false);
  };
  const handleConfirmDuplicateEdit = () => {
    if (userRole.includes('IPC')) {
      updateEditDuplicateVisibility(false);
      updateShowAddNewContactDialog(false);
      updateEditVisibility(true);
    } else {
      associateMrdRequest({
        contactId: rowToEdit.id,
        impcCode: selectedMrdImpc,
      });
    }
  };

  const columns = MrdContactsColumns(data, userRole, onRowDelete, onRowEdit);

  return (
    <MrdContactsStyles>
      <div className="mrd-contacts-container">
        <div className="mrd-contact-table-container">
          <div className="left-table">
            <ListTable
              listId={TableDefinitions.MRD_IMPC_LIST_BY_COUNTRY.id}
              columns={mrdImpcColumns}
              loading={mrdImpcIsLoading}
              data={mrdImpcData}
              getTrProps={getOnClickOfMrdImpcTable}
            />
          </div>
          {mrdImpcData.length > 0 && (
            <div className="right-table">
              <div className="mrd-contact-right-button-container">
                {
                  (userRole === 'IPC_ADMIN' || userRole === 'OPER_ADMIN') && (
                    <Button
                      onClick={openNewContactDialog}
                      className="primary-button-false mrd-contact-button"
                    >
                      New contact
                    </Button>
                  )
                }
                {(userRole === 'IPC_ADMIN' || userRole === 'OPER_ADMIN') && selectedMrdImpc !== '' && (
                  <Button
                    onClick={() => { updateCopyContactsStatus(true); }}
                    className={`primary-button-${!mrdImpcData.map((item) => item.impc).includes(selectedMrdImpc)} mrd-contact-button`}
                    disabled={!mrdImpcData.map((item) => item.impc).includes(selectedMrdImpc)}
                  >
                    Copy contacts from IMPC
                  </Button>
                )}
              </div>
              <ListTable
                listId={TableDefinitions.MRD_CONTACTS.id}
                loading={isLoading}
                columns={columns}
                data={data}
              />
              <ContactCopyDialog
                isOpen={showCopyContactsDialog}
                targetImpc={selectedMrdImpc}
                handleConfirm={copyContactConfirm}
                handleCancel={() => { updateCopyContactsStatus(false); }}
              />
              { editVisible && (
                <EditContactDialog
                  isOpen={editVisible}
                  closeDialog={() => { updateEditVisibility(false); }}
                  handleCancel={() => { updateEditVisibility(false); }}
                  confirmHandle={confirmEditContact}
                  selectedContact={rowToEdit}
                />
              )}
              {showAddNewContactDialog && (
                <MrdContactsCreateDialog
                  isOpen={showAddNewContactDialog}
                  closeDialog={closeNewContactDialog}
                  selectedImpc={selectedMrdImpc}
                  handleCancel={() => { updateShowAddNewContactDialog(false); }}
                  addNewContact={confirmAddNewContact}
                />
              )}
            </div>
          )}
        </div>
      </div>
      <ConfirmationDialog
        isOpen={deleteVisible}
        submitButtonText="Yes"
        cancelButtonText="No"
        onClose={handleDeleteDialogCancel}
        onSubmit={handleConfirmDelete}
        confirmationTitle={formatMessage('en', 'contact.mrd.delete.active.label')}
      />
      <ConfirmationDialog
        isOpen={editDuplicateVisible}
        submitButtonText="Yes"
        cancelButtonText="No"
        onClose={handleEditDuplicateDialogCancel}
        onSubmit={handleConfirmDuplicateEdit}
        confirmationTitle={userRole.includes('IPC_ADMIN') ? formatMessage('en', 'contact.edit.existing.ipc.label') : formatMessage('en', 'contact.edit.existing.operator.label')}
      />
    </MrdContactsStyles>
  );
}

function mapStateToProps(state) {
  return {
    data: getMrdContactsData(state),
    isLoading: getMrdContactsIsLoading(state),
    mrdImpcData: getMrdContactsImpcData(state),
    mrdImpcColumns: getMrdContactsImpcColumns(state),
    mrdImpcIsLoading: getMrdContactsImpcIsLoading(state),
    userRole: getUserRole(state),
    deleteContactRequestStatus: getDeleteContactRequestLoading(state),
    preservedImpc: getContactIMPC(state),
  };
}

MrdContactsContainer.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  mrdImpcData: PropTypes.arrayOf(PropTypes.object),
  mrdImpcColumns: PropTypes.arrayOf(PropTypes.object),
  mrdImpcIsLoading: PropTypes.bool,
  tableLoad: PropTypes.func,
  copyContactsRequest: PropTypes.func,
  userRole: PropTypes.string,
  deleteContact: PropTypes.func,
  addContact: PropTypes.func,
  editContact: PropTypes.func,
  preservedImpc: PropTypes.string,
  associateMrdRequest: PropTypes.func,
};

const mapDispatchToProps = (dispatch) => bindActionCreators({
  tableLoad: tableLoadData,
  copyContactsRequest: postCopyContactRequestActionCreator,
  deleteContact: deleteContactRequestActionCreator,
  addContact: addNewContactRequestActionCreator,
  editContact: editContactRequestActionCreator,
  associateMrdRequest: associateMrdContactsRequestActionCreator,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(MrdContactsContainer);
